<template>
  <div class="content">
    <h1 class="has-text-centered">
      AHL Points Comparison
    </h1>
    <hr>
    <div
      v-if="!userOneLoaded || !userTwoLoaded || !userThreeLoaded"
      class="spinner-loader"
    >
      <sync-loader />
      <p>Loading ...</p>
    </div>
    <hr>
    <div class="columns">
      <div
        v-if="userOne && userTwo && userThree"
        class="column is-4"
      >
        <div class="box has-text-centered">
          <div
            class="is-pulled-right"
            style="cursor: pointer"
            @click="removePlayer(1)"
          >
            x
          </div>
          <strong>{{ userOne.general.name }}</strong>
          <div class="columns">
            <div class="column is-12">
              <div class="field has-addons">
                <div class="control">
                  <input
                    v-model="userOneId"
                    class="input is-small"
                    type="text"
                    placeholder="AHL#"
                    @keyup.enter="compare(userOneId, userTwoId, userThreeId)"
                  >
                </div>
                <div
                  class="control"
                  @click="compare(userOneId, userTwoId, userThreeId)"
                >
                  <a class="button is-black is-small">
                    <span class="icon is-right">
                      <i class="far fa-search" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <p><strong class="has-text-info">{{ userOne.general.ahlid }}</strong></p>
          <div class="img-avatar">
            <img
              v-if="userOne.general.photo"
              :src="userOne.general.photo"
            >
            <img
              v-if="!userOne.general.photo"
              src="/images/profile.png"
            >
          </div>
          <div>
            <p v-if="userOne.rankings.overall">
              Total Points <strong>{{ userOne.rankings.overall.points }}</strong>
            </p>
            <p v-if="!userOne.rankings.overall">
              Total Points <strong>None</strong>
            </p>
            <p v-if="userOne.rankings.overall">
              Total Rank <strong>#{{ userOne.rankings.overall.rank }}</strong>
            </p>
            <p v-if="!userOne.rankings.overall">
              Total Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.overall < 30,
                'is-info': userOnePercentage.overall >= 30 && userOnePercentage.overall <= 70,
                'is-success': userOnePercentage.overall > 70
              }"
              :value="userOnePercentage.overall"
              max="100"
            />
          </div>
          <div>
            <p v-if="userOne.rankings.thisYear">
              This Year Points <strong>{{ userOne.rankings.thisYear.points }}</strong>
            </p>
            <p v-if="!userOne.rankings.thisYear">
              This Year Points <strong>None</strong>
            </p>
            <p v-if="userOne.rankings.thisYear">
              This Year Rank <strong>#{{ userOne.rankings.thisYear.rank }}</strong>
            </p>
            <p v-if="!userOne.rankings.thisYear">
              This Year Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.thisYear < 30,
                'is-info': userOnePercentage.thisYear >= 30 && userOnePercentage.thisYear <= 70,
                'is-success': userOnePercentage.thisYear > 70
              }"
              :value="userOnePercentage.thisYear"
              max="100"
            />
          </div>
          <div>
            <p v-if="userOne.rankings.thisMonth">
              This Month Points
              <strong>{{ userOne.rankings.thisMonth.points }}</strong>
            </p>
            <p v-if="!userOne.rankings.thisMonth">
              This Month Points <strong>None</strong>
            </p>
            <p v-if="userOne.rankings.thisMonth">
              This Month Rank <strong>#{{ userOne.rankings.thisMonth.rank }}</strong>
            </p>
            <p v-if="!userOne.rankings.thisMonth">
              This Month Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.thisMonth < 30,
                'is-info': userOnePercentage.thisMonth >= 30 && userOnePercentage.thisMonth <= 70,
                'is-success': userOnePercentage.thisMonth > 70
              }"
              :value="userOnePercentage.thisMonth"
              max="100"
            />
          </div>
          <div>
            <p v-if="userOne.rankings.lastMonth">
              Last Month Points
              <strong>{{ userOne.rankings.lastMonth.points }}</strong>
            </p>
            <p v-if="!userOne.rankings.lastMonth">
              Last Month Points <strong>None</strong>
            </p>
            <p v-if="userOne.rankings.lastMonth">
              Last Month Rank <strong>#{{ userOne.rankings.lastMonth.rank }}</strong>
            </p>
            <p v-if="!userOne.rankings.lastMonth">
              Last Month Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.lastMonth < 30,
                'is-info': userOnePercentage.lastMonth >= 30 && userOnePercentage.lastMonth <= 70,
                'is-success': userOnePercentage.lastMonth > 70
              }"
              :value="userOnePercentage.lastMonth"
              max="100"
            />
          </div>
          <hr>
          <div v-if="userOne.totalPlacements">
            <p v-if="userOne.totalPlacements[0].count">
              1st Place <strong>{{ userOne.totalPlacements[0].count }}</strong>
            </p>
            <p v-if="!userOne.totalPlacements[0].count">
              1st Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.first < 30,
                'is-info': userOnePercentage.first >= 30 && userOnePercentage.first <= 70,
                'is-success': userOnePercentage.first > 70
              }"
              :value="userOnePercentage.first"
              max="100"
            />
          </div>
          <div v-if="userOne.totalPlacements">
            <p v-if="userOne.totalPlacements[1].count">
              2nd Place <strong>{{ userOne.totalPlacements[1].count }}</strong>
            </p>
            <p v-if="!userOne.totalPlacements[1].count">
              2nd Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.second < 30,
                'is-info': userOnePercentage.second >= 30 && userOnePercentage.second <= 70,
                'is-success': userOnePercentage.second > 70
              }"
              :value="userOnePercentage.second"
              max="100"
            />
          </div>
          <div v-if="userOne.totalPlacements">
            <p v-if="userOne.totalPlacements[2].count">
              3rd Place <strong>{{ userOne.totalPlacements[2].count }}</strong>
            </p>
            <p v-if="!userOne.totalPlacements[2].count">
              3rd Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.third < 30,
                'is-info': userOnePercentage.third >= 30 && userOnePercentage.third <= 70,
                'is-success': userOnePercentage.third > 70
              }"
              :value="userOnePercentage.third"
              max="100"
            />
          </div>
          <div v-if="userOne.totalPlacements">
            <p v-if="userOne.totalPlacements[3].count">
              4th Place <strong>{{ userOne.totalPlacements[3].count }}</strong>
            </p>
            <p v-if="!userOne.totalPlacements[3].count">
              4th Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.fourth < 30,
                'is-info': userOnePercentage.fourth >= 30 && userOnePercentage.fourth <= 70,
                'is-success': userOnePercentage.fourth > 70
              }"
              :value="userOnePercentage.fourth"
              max="100"
            />
          </div>
          <div v-if="userOne.totalPlacements">
            <p v-if="userOne.totalPlacements[4].count">
              5th Place <strong>{{ userOne.totalPlacements[4].count }}</strong>
            </p>
            <p v-if="!userOne.totalPlacements[4].count">
              5th Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userOnePercentage.fifth < 30,
                'is-info': userOnePercentage.fifth >= 30 && userOnePercentage.fifth <= 70,
                'is-success': userOnePercentage.fifth > 70
              }"
              :value="userOnePercentage.fifth"
              max="100"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!userOne && userOneLoaded"
        class="column is-4"
      >
        <div class="box has-text-centered">
          <strong>AHL ID is incorrect, please check it</strong>
          <div class="columns">
            <div class="column is-12">
              <div class="field has-addons">
                <div class="control">
                  <input
                    v-model="userOneId"
                    class="input is-small"
                    type="text"
                    placeholder="AHL#"
                    @keyup.enter="compare(userOneId, userTwoId, userThreeId)"
                  >
                </div>
                <div
                  class="control"
                  @click="compare(userOneId, userTwoId, userThreeId)"
                >
                  <a class="button is-black is-small">
                    <span class="icon is-right">
                      <i class="far fa-search" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="userOne && userTwo && userThree"
        class="column is-4"
      >
        <div class="box has-text-centered">
          <div
            class="is-pulled-right"
            style="cursor: pointer"
            @click="removePlayer(2)"
          >
            x
          </div>
          <strong>{{ userTwo.general.name }}</strong>
          <div class="columns">
            <div class="column is-12">
              <div class="field has-addons">
                <div class="control">
                  <input
                    v-model="userTwoId"
                    class="input is-small"
                    type="text"
                    placeholder="AHL#"
                    @keyup.enter="compare(userOneId, userTwoId, userThreeId)"
                  >
                </div>
                <div
                  class="control"
                  @click="compare(userOneId, userTwoId, userThreeId)"
                >
                  <a class="button is-black is-small">
                    <span class="icon is-right">
                      <i class="far fa-search" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <p><strong class="has-text-info">{{ userTwo.general.ahlid }}</strong></p>
          <div class="img-avatar">
            <img
              v-if="userTwo.general.photo"
              :src="userTwo.general.photo"
            >
            <img
              v-if="!userTwo.general.photo"
              src="/images/profile.png"
            >
          </div>
          <div>
            <p v-if="userTwo.rankings.overall">
              Total Points <strong>{{ userTwo.rankings.overall.points }}</strong>
            </p>
            <p v-if="!userTwo.rankings.overall">
              Total Points <strong>None</strong>
            </p>
            <p v-if="userTwo.rankings.overall">
              Total Rank <strong>#{{ userTwo.rankings.overall.rank }}</strong>
            </p>
            <p v-if="!userTwo.rankings.overall">
              Total Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.overall < 30,
                'is-info': userTwoPercentage.overall >= 30 && userTwoPercentage.overall <= 70,
                'is-success': userTwoPercentage.overall > 70
              }"
              :value="userTwoPercentage.overall"
              max="100"
            />
          </div>
          <div>
            <p v-if="userTwo.rankings.thisYear">
              This Year Points <strong>{{ userTwo.rankings.thisYear.points }}</strong>
            </p>
            <p v-if="!userTwo.rankings.thisYear">
              This Year Points <strong>None</strong>
            </p>
            <p v-if="userTwo.rankings.thisYear">
              This Year Rank <strong>#{{ userTwo.rankings.thisYear.rank }}</strong>
            </p>
            <p v-if="!userTwo.rankings.thisYear">
              This Year Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.thisYear < 30,
                'is-info': userTwoPercentage.thisYear >= 30 && userTwoPercentage.thisYear <= 70,
                'is-success': userTwoPercentage.thisYear > 70
              }"
              :value="userTwoPercentage.thisYear"
              max="100"
            />
          </div>
          <div>
            <p v-if="userTwo.rankings.thisMonth">
              This Month Points
              <strong>{{ userTwo.rankings.thisMonth.points }}</strong>
            </p>
            <p v-if="!userTwo.rankings.thisMonth">
              This Month Points <strong>None</strong>
            </p>
            <p v-if="userTwo.rankings.thisMonth">
              This Month Rank <strong>#{{ userTwo.rankings.thisMonth.rank }}</strong>
            </p>
            <p v-if="!userTwo.rankings.thisMonth">
              This Month Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.thisMonth < 30,
                'is-info': userTwoPercentage.thisMonth >= 30 && userTwoPercentage.thisMonth <= 70,
                'is-success': userTwoPercentage.thisMonth > 70
              }"
              :value="userTwoPercentage.thisMonth"
              max="100"
            />
          </div>
          <div>
            <p v-if="userTwo.rankings.lastMonth">
              Last Month Points
              <strong>{{ userTwo.rankings.lastMonth.points }}</strong>
            </p>
            <p v-if="!userTwo.rankings.lastMonth">
              Last Month Points <strong>None</strong>
            </p>
            <p v-if="userTwo.rankings.lastMonth">
              Last Month Rank <strong>#{{ userTwo.rankings.lastMonth.rank }}</strong>
            </p>
            <p v-if="!userTwo.rankings.lastMonth">
              Last Month Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.lastMonth < 30,
                'is-info': userTwoPercentage.lastMonth >= 30 && userTwoPercentage.lastMonth <= 70,
                'is-success': userTwoPercentage.lastMonth > 70
              }"
              :value="userTwoPercentage.lastMonth"
              max="100"
            />
          </div>
          <hr>
          <div v-if="userTwo.totalPlacements">
            <p v-if="userTwo.totalPlacements[0].count">
              1st Place <strong>{{ userTwo.totalPlacements[0].count }}</strong>
            </p>
            <p v-if="!userTwo.totalPlacements[0].count">
              1st Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.first < 30,
                'is-info': userTwoPercentage.first >= 30 && userTwoPercentage.first <= 70,
                'is-success': userTwoPercentage.first > 70
              }"
              :value="userTwoPercentage.first"
              max="100"
            />
          </div>
          <div v-if="userTwo.totalPlacements">
            <p v-if="userTwo.totalPlacements[1].count">
              2nd Place <strong>{{ userTwo.totalPlacements[1].count }}</strong>
            </p>
            <p v-if="!userTwo.totalPlacements[1].count">
              2nd Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.second < 30,
                'is-info': userTwoPercentage.second >= 30 && userTwoPercentage.second <= 70,
                'is-success': userTwoPercentage.second > 70
              }"
              :value="userTwoPercentage.second"
              max="100"
            />
          </div>
          <div v-if="userTwo.totalPlacements">
            <p v-if="userTwo.totalPlacements[2].count">
              3rd Place <strong>{{ userTwo.totalPlacements[2].count }}</strong>
            </p>
            <p v-if="!userTwo.totalPlacements[2].count">
              3rd Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.third < 30,
                'is-info': userTwoPercentage.third >= 30 && userTwoPercentage.third <= 70,
                'is-success': userTwoPercentage.third > 70
              }"
              :value="userTwoPercentage.third"
              max="100"
            />
          </div>
          <div v-if="userTwo.totalPlacements">
            <p v-if="userTwo.totalPlacements[3].count">
              4th Place <strong>{{ userTwo.totalPlacements[3].count }}</strong>
            </p>
            <p v-if="!userTwo.totalPlacements[3].count">
              4th Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.fourth < 30,
                'is-info': userTwoPercentage.fourth >= 30 && userTwoPercentage.fourth <= 70,
                'is-success': userTwoPercentage.fourth > 70
              }"
              :value="userTwoPercentage.fourth"
              max="100"
            />
          </div>
          <div v-if="userTwo.totalPlacements">
            <p v-if="userTwo.totalPlacements[4].count">
              5th Place <strong>{{ userTwo.totalPlacements[4].count }}</strong>
            </p>
            <p v-if="!userTwo.totalPlacements[4].count">
              5th Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userTwoPercentage.fifth < 30,
                'is-info': userTwoPercentage.fifth >= 30 && userTwoPercentage.fifth <= 70,
                'is-success': userTwoPercentage.fifth > 70
              }"
              :value="userTwoPercentage.fifth"
              max="100"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!userTwo && userTwoLoaded"
        class="column is-4"
      >
        <div class="box has-text-centered">
          <strong>AHL ID is incorrect, please check it</strong>
          <div class="columns">
            <div class="column is-12">
              <div class="field has-addons">
                <div class="control">
                  <input
                    v-model="userOneId"
                    class="input is-small"
                    type="text"
                    placeholder="AHL#"
                    @keyup.enter="compare(userOneId, userTwoId, userThreeId)"
                  >
                </div>
                <div
                  class="control"
                  @click="compare(userOneId, userTwoId, userThreeId)"
                >
                  <a class="button is-black is-small">
                    <span class="icon is-right">
                      <i class="far fa-search" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="userOne && userTwo && userThree"
        class="column is-4"
      >
        <div class="box has-text-centered">
          <div
            class="is-pulled-right"
            style="cursor: pointer"
            @click="removePlayer(3)"
          >
            x
          </div>
          <strong>{{ userThree.general.name }}</strong>
          <div class="columns">
            <div class="column is-12">
              <div class="field has-addons">
                <div class="control">
                  <input
                    v-model="userThreeId"
                    class="input is-small"
                    type="text"
                    placeholder="AHL#"
                    @keyup.enter="compare(userOneId, userTwoId, userThreeId)"
                  >
                </div>
                <div
                  class="control"
                  @click="compare(userOneId, userTwoId, userThreeId)"
                >
                  <a class="button is-black is-small">
                    <span class="icon is-right">
                      <i class="far fa-search" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <p><strong class="has-text-info">{{ userThree.general.ahlid }}</strong></p>
          <div class="img-avatar">
            <img
              v-if="userThree.general.photo"
              :src="userThree.general.photo"
            >
            <img
              v-if="!userThree.general.photo"
              src="/images/profile.png"
            >
          </div>
          <div>
            <p v-if="userThree.rankings.overall">
              Total Points <strong>{{ userThree.rankings.overall.points }}</strong>
            </p>
            <p v-if="!userThree.rankings.overall">
              Total Points <strong>None</strong>
            </p>
            <p v-if="userThree.rankings.overall">
              Total Rank <strong>#{{ userThree.rankings.overall.rank }}</strong>
            </p>
            <p v-if="!userThree.rankings.overall">
              Total Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.overall < 30,
                'is-info': userThreePercentage.overall >= 30 && userThreePercentage.overall <= 70,
                'is-success': userThreePercentage.overall > 70
              }"
              :value="userThreePercentage.overall"
              max="100"
            />
          </div>
          <div>
            <p v-if="userThree.rankings.thisYear">
              This Year Points
              <strong>{{ userThree.rankings.thisYear.points }}</strong>
            </p>
            <p v-if="!userThree.rankings.thisYear">
              This Year Points <strong>None</strong>
            </p>
            <p v-if="userThree.rankings.thisYear">
              This Year Rank <strong>#{{ userThree.rankings.thisYear.rank }}</strong>
            </p>
            <p v-if="!userThree.rankings.thisYear">
              This Year Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.thisYear < 30,
                'is-info': userThreePercentage.thisYear >= 30 && userThreePercentage.thisYear <= 70,
                'is-success': userThreePercentage.thisYear > 70
              }"
              :value="userThreePercentage.thisYear"
              max="100"
            />
          </div>
          <div>
            <p v-if="userThree.rankings.thisMonth">
              This Month Points
              <strong>{{ userThree.rankings.thisMonth.points }}</strong>
            </p>
            <p v-if="!userThree.rankings.thisMonth">
              This Month Points <strong>None</strong>
            </p>
            <p v-if="userThree.rankings.thisMonth">
              This Month Rank
              <strong>#{{ userThree.rankings.thisMonth.rank }}</strong>
            </p>
            <p v-if="!userThree.rankings.thisMonth">
              This Month Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.thisMonth < 30,
                'is-info': userThreePercentage.thisMonth >= 30 && userThreePercentage.thisMonth <= 70,
                'is-success': userThreePercentage.thisMonth > 70
              }"
              :value="userThreePercentage.thisMonth"
              max="100"
            />
          </div>
          <div>
            <p v-if="userThree.rankings.lastMonth">
              Last Month Points
              <strong>{{ userThree.rankings.lastMonth.points }}</strong>
            </p>
            <p v-if="!userThree.rankings.lastMonth">
              Last Month Points <strong>None</strong>
            </p>
            <p v-if="userThree.rankings.lastMonth">
              Last Month Rank
              <strong>#{{ userThree.rankings.lastMonth.rank }}</strong>
            </p>
            <p v-if="!userThree.rankings.lastMonth">
              Last Month Rank <strong>#None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.lastMonth < 30,
                'is-info': userThreePercentage.lastMonth >= 30 && userThreePercentage.lastMonth <= 70,
                'is-success': userThreePercentage.lastMonth > 70
              }"
              :value="userThreePercentage.lastMonth"
              max="100"
            />
          </div>
          <hr>
          <div v-if="userThree.totalPlacements">
            <p v-if="userThree.totalPlacements[0].count">
              1st Place
              <strong>{{ userThree.totalPlacements[0].count }}</strong>
            </p>
            <p v-if="!userThree.totalPlacements[0].count">
              1st Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.first < 30,
                'is-info': userThreePercentage.first >= 30 && userThreePercentage.first <= 70,
                'is-success': userThreePercentage.first > 70
              }"
              :value="userThreePercentage.first"
              max="100"
            />
          </div>
          <div v-if="userThree.totalPlacements">
            <p v-if="userThree.totalPlacements[1].count">
              2nd Place
              <strong>{{ userThree.totalPlacements[1].count }}</strong>
            </p>
            <p v-if="!userThree.totalPlacements[1].count">
              2nd Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.second < 30,
                'is-info': userThreePercentage.second >= 30 && userThreePercentage.second <= 70,
                'is-success': userThreePercentage.second > 70
              }"
              :value="userThreePercentage.second"
              max="100"
            />
          </div>
          <div v-if="userThree.totalPlacements">
            <p v-if="userThree.totalPlacements[2].count">
              3rd Place
              <strong>{{ userThree.totalPlacements[2].count }}</strong>
            </p>
            <p v-if="!userThree.totalPlacements[2].count">
              3rd Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.third < 30,
                'is-info': userThreePercentage.third >= 30 && userThreePercentage.third <= 70,
                'is-success': userThreePercentage.third > 70
              }"
              :value="userThreePercentage.third"
              max="100"
            />
          </div>
          <div v-if="userThree.totalPlacements">
            <p v-if="userThree.totalPlacements[3].count">
              4th Place
              <strong>{{ userThree.totalPlacements[3].count }}</strong>
            </p>
            <p v-if="!userThree.totalPlacements[3].count">
              4th Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.fourth < 30,
                'is-info': userThreePercentage.fourth >= 30 && userThreePercentage.fourth <= 70,
                'is-success': userThreePercentage.fourth > 70
              }"
              :value="userThreePercentage.fourth"
              max="100"
            />
          </div>
          <div v-if="userThree.totalPlacements">
            <p v-if="userThree.totalPlacements[4].count">
              5th Place
              <strong>{{ userThree.totalPlacements[4].count }}</strong>
            </p>
            <p v-if="!userThree.totalPlacements[4].count">
              5th Place <strong>None</strong>
            </p>
            <progress
              class="progress is-small"
              :class="{
                'is-danger': userThreePercentage.fifth < 30,
                'is-info': userThreePercentage.fifth >= 30 && userThreePercentage.fifth <= 70,
                'is-success': userThreePercentage.fifth > 70
              }"
              :value="userThreePercentage.fifth"
              max="100"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!userThree && userThreeLoaded"
        class="column is-4"
      >
        <div class="box has-text-centered">
          <strong>AHL ID is incorrect, please check it</strong>
          <div class="columns">
            <div class="column is-12">
              <div class="field has-addons">
                <div class="control">
                  <input
                    v-model="userOneId"
                    class="input is-small"
                    type="text"
                    placeholder="AHL#"
                    @keyup.enter="compare(userOneId, userTwoId, userThreeId)"
                  >
                </div>
                <div
                  class="control"
                  @click="compare(userOneId, userTwoId, userThreeId)"
                >
                  <a class="button is-black is-small">
                    <span class="icon is-right">
                      <i class="far fa-search" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'

  export default {
    name: 'ComparitionThree',
    components: {
    },
    data() {
      return {
        userOne: '',
        userTwo: '',
        userThree: '',
        userOneId: '',
        userTwoId: '',
        userThreeId: '',
        userOneLoaded: false,
        userTwoLoaded: false,
        userThreeLoaded: false
      }
    },
    computed: {
      userOnePercentage() {
        let overall, thisYear, thisMonth, lastMonth, first, second, third, fourth, fifth
        // Overall
        overall = this.percentage(this.userOne.rankings.overall.points, this.userTwo.rankings.overall.points, this.userThree.rankings.overall.points)
        // This year
        thisYear = this.percentage(this.userOne.rankings.thisYear.points, this.userTwo.rankings.thisYear.points, this.userThree.rankings.thisYear.points)
        // This Month
        thisMonth = this.percentage(this.userOne.rankings.thisMonth.points, this.userTwo.rankings.thisMonth.points, this.userThree.rankings.thisMonth.points)
        // Last month
        lastMonth = this.percentage(this.userOne.rankings.lastMonth.points, this.userTwo.rankings.lastMonth.points, this.userThree.rankings.lastMonth.points)
        first = this.percentage(this.userOne.totalPlacements[0].count, this.userTwo.totalPlacements[0].count, this.userThree.totalPlacements[0].count)
        second = this.percentage(this.userOne.totalPlacements[1].count, this.userTwo.totalPlacements[1].count, this.userThree.totalPlacements[0].count)
        third = this.percentage(this.userOne.totalPlacements[2].count, this.userTwo.totalPlacements[2].count, this.userThree.totalPlacements[0].count)
        fourth = this.percentage(this.userOne.totalPlacements[3].count, this.userTwo.totalPlacements[3].count, this.userThree.totalPlacements[0].count)
        fifth = this.percentage(this.userOne.totalPlacements[4].count, this.userTwo.totalPlacements[4].count, this.userThree.totalPlacements[0].count)
        return {
          overall: overall,
          thisYear: thisYear,
          thisMonth: thisMonth,
          lastMonth: lastMonth,
          first,
          second,
          third,
          fourth,
          fifth
        }
      },
      userTwoPercentage() {
        let overall, thisYear, thisMonth, lastMonth, first, second, third, fourth, fifth
        // Overall
        overall = this.percentage(this.userTwo.rankings.overall.points, this.userOne.rankings.overall.points, this.userThree.rankings.overall.points)
        // This year
        thisYear = this.percentage(this.userTwo.rankings.thisYear.points, this.userOne.rankings.thisYear.points, this.userThree.rankings.thisYear.points)
        // This Month
        thisMonth = this.percentage(this.userTwo.rankings.thisMonth.points, this.userOne.rankings.thisMonth.points, this.userThree.rankings.thisMonth.points)
        // Last month
        lastMonth = this.percentage(this.userTwo.rankings.lastMonth.points, this.userOne.rankings.lastMonth.points, this.userThree.rankings.lastMonth.points)
        first = this.percentage(this.userTwo.totalPlacements[0].count, this.userOne.totalPlacements[0].count, this.userThree.totalPlacements[0].count)
        second = this.percentage(this.userTwo.totalPlacements[1].count, this.userOne.totalPlacements[1].count, this.userThree.totalPlacements[0].count)
        third = this.percentage(this.userTwo.totalPlacements[2].count, this.userOne.totalPlacements[2].count, this.userThree.totalPlacements[0].count)
        fourth = this.percentage(this.userTwo.totalPlacements[3].count, this.userOne.totalPlacements[3].count, this.userThree.totalPlacements[0].count)
        fifth = this.percentage(this.userTwo.totalPlacements[4].count, this.userOne.totalPlacements[4].count, this.userThree.totalPlacements[0].count)
        return {
          overall: overall,
          thisYear: thisYear,
          thisMonth: thisMonth,
          lastMonth: lastMonth,
          first,
          second,
          third,
          fourth,
          fifth
        }
      },
      userThreePercentage() {
        let overall, thisYear, thisMonth, lastMonth, first, second, third, fourth, fifth
        // Overall
        overall = this.percentage(this.userThree.rankings.overall.points, this.userOne.rankings.overall.points, this.userTwo.rankings.overall.points)
        // This year
        thisYear = this.percentage(this.userThree.rankings.thisYear.points, this.userOne.rankings.thisYear.points, this.userTwo.rankings.thisYear.points)
        // This Month
        thisMonth = this.percentage(this.userThree.rankings.thisMonth.points, this.userOne.rankings.thisMonth.points, this.userTwo.rankings.thisMonth.points)
        // Last month
        lastMonth = this.percentage(this.userThree.rankings.lastMonth.points, this.userOne.rankings.lastMonth.points, this.userTwo.rankings.lastMonth.points)
        first = this.percentage(this.userThree.totalPlacements[0].count, this.userOne.totalPlacements[0].count, this.userTwo.totalPlacements[0].count)
        second = this.percentage(this.userThree.totalPlacements[1].count, this.userOne.totalPlacements[1].count, this.userTwo.totalPlacements[0].count)
        third = this.percentage(this.userThree.totalPlacements[2].count, this.userOne.totalPlacements[2].count, this.userTwo.totalPlacements[0].count)
        fourth = this.percentage(this.userThree.totalPlacements[3].count, this.userOne.totalPlacements[3].count, this.userTwo.totalPlacements[0].count)
        fifth = this.percentage(this.userThree.totalPlacements[4].count, this.userOne.totalPlacements[4].count, this.userTwo.totalPlacements[0].count)
        return {
          overall: overall,
          thisYear: thisYear,
          thisMonth: thisMonth,
          lastMonth: lastMonth,
          first,
          second,
          third,
          fourth,
          fifth
        }
      }
    },
    mounted() {
      this.initialize()
    },
    methods: {
      initialize() {
        let userOne = this.$route.params.userOne
        let userTwo = this.$route.params.userTwo
        let userThree = this.$route.params.userThree
        this.userOneId = userOne
        this.userTwoId = userTwo
        this.userThreeId = userThree
        this.findUser('first', userOne)
        this.findUser('second', userTwo)
        this.findUser('third', userThree)
      },
      findUser(user, id) {
        this.$axios({
          method: 'POST',
          url: `general/memberpoints/${id}`,
          headers: {
            Authorization: '84cd7a9a97d4c1d8f6402f9389711560',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          data: {
            token: this.token || ''
          }
        })
          .then((res) => {
            if (user === 'first') {
              this.userOneLoaded = true
              this.userOne = this.recompileUserObject(res.data)
              this.userOneId = res.data.general.ahlid
            } else if (user === 'second') {
              this.userTwoLoaded = true
              this.userTwo = this.recompileUserObject(res.data)
              this.userTwoId = res.data.general.ahlid
            } else {
              this.userThreeLoaded = true
              this.userThree = this.recompileUserObject(res.data)
              this.userThreeId = res.data.general.ahlid
            }
          })
          .catch((error) => {
            this.$emit('message', {error: true, message: error.response.data.message})
          })
      },
      percentage(firstNumber, secondNumber, thirdNumber) {
        if (+secondNumber + +firstNumber + +thirdNumber === 0) return 0
        return Math.round(firstNumber * 100 / (+secondNumber + +firstNumber + +thirdNumber))
      },
      recompileUserObject(user) {
        let rankingsLastMonth = user.rankings.lastMonth
        let rankingsOverall = user.rankings.overall
        let rankingsThisMonth = user.rankings.thisMonth
        let rankingsThisYear = user.rankings.thisYear
        var topFive = []
        if (!rankingsLastMonth) {
          rankingsLastMonth = {
            points: 0,
            rank: 0
          }
        }
        if (!rankingsOverall) {
          rankingsOverall = {
            points: 0,
            rank: 0
          }
        }
        if (!rankingsThisMonth) {
          rankingsThisMonth = {
            points: 0,
            rank: 0
          }
        }
        if (!rankingsThisYear) {
          rankingsThisYear = {
            points: 0,
            rank: 0
          }
        }
        for (let i = 1; i <= 5; i++) {
          let found = false
          for (let j = 0; j < user.totalPlacements.length; j++) {
            if (i === +user.totalPlacements[j].place) {
              topFive.push({
                count: user.totalPlacements[j].count,
                place: i
              })
              found = true
            }
          }
          if (!found) {
            topFive.push({
              count: 0,
              place: i
            })
          }
        }
        return {
          general: user.general,
          rankings: {
            lastMonth: rankingsLastMonth,
            overall: rankingsOverall,
            thisMonth: rankingsThisMonth,
            thisYear: rankingsThisYear
          },
          totalPlacements: topFive
        }
      },
      removePlayer(number) {
        if (number === 1) {
          this.$router.push(`/comparison/${this.userTwoId}/${this.userThreeId}`)
        } else if (number === 2) {
          this.$router.push(`/comparison/${this.userOneId}/${this.userThreeId}`)
        } else if (number === 3) {
          this.$router.push(`/comparison/${this.userOneId}/${this.userTwoId}`)
        }
      },
      compare(userOneId, userTwoId, userThreeId) {
        this.$router.push(`/comparison/${this.userOneId}/${this.userTwoId}/${this.userThreeId}`)
        this.userOne = ''
        this.userTwo = ''
        this.userThree = ''
        this.userOneLoaded = false
        this.userTwoLoaded = false
        this.userThreeLoaded = false
        this.initialize()
      }
    }
  }
</script>
